import React from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Eye, EyeOff, MessageSquare, Phone, Shield } from 'lucide-react';
import { EASE_OUT, KitError, KitField, KitLabel, KitPrimaryButton, KitSecondaryButton } from '../ui/premiumKit';
import { OtpSpamHint } from './OtpSpamHint';
import { SignupConsentChecklist } from './SignupConsentChecklist';

// 로그인·가입 화면의 두 단계(전화번호 → 인증번호) 그리기 전용 — 2026-11-03 새 디자인(대표 "다른 부분들도 구식 UI … 지금처럼").
// 기준: 대표 확정 최고 기준(10/14 안내서). 동작(전송·확인·재전송·검사)은 AuthPage에 그대로 있고 여기는 받은 값만 그린다.
// 글귀는 예전 화면 그대로다(하나도 빼거나 바꾸지 않는다 — 11/3 '진행률' 교훈).

const Spinner: React.FC<{ dark?: boolean }> = ({ dark }) => (
    <span className={`h-4 w-4 animate-spin rounded-full border-2 ${dark ? 'border-black/25 border-t-black' : 'border-white/30 border-t-white'}`} />
);

const FieldError: React.FC<{ message?: string }> = ({ message }) => {
    const reduce = useReducedMotion();
    return (
        <AnimatePresence initial={false}>
            {message && (
                <motion.div
                    initial={reduce ? false : { opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25, ease: EASE_OUT }}
                    className="mt-2"
                >
                    <KitError>{message}</KitError>
                </motion.div>
            )}
        </AnimatePresence>
    );
};

export interface AuthPhoneFormProps {
    phone: string;
    onPhoneChange: (raw: string) => void;
    onPhoneBlur: () => void;
    phoneError?: string;
    isFormValid: boolean;
    sending: boolean;
    isSignUp: boolean;
    onSubmit: (e: React.FormEvent) => void;
    onToggleMode: () => void;
}

/** 1단계 — 전화번호 넣고 [인증번호 전송] */
export const AuthPhoneForm: React.FC<AuthPhoneFormProps> = ({
    phone, onPhoneChange, onPhoneBlur, phoneError, isFormValid, sending, isSignUp, onSubmit, onToggleMode,
}) => {
    const canSend = isFormValid && !sending;
    return (
        <form onSubmit={onSubmit} className="mt-8">
            <KitLabel htmlFor="auth-phone">전화번호</KitLabel>
            <KitField className="mt-2" icon={<Phone className="h-5 w-5" />} state={phoneError ? 'error' : 'default'}>
                <input
                    id="auth-phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => onPhoneChange(e.target.value)}
                    onBlur={onPhoneBlur}
                    className="w-full bg-transparent text-[18px] font-medium tracking-wide text-[#FAFAFA] placeholder-[#6B6B74] focus:outline-none"
                    style={{ fontVariantNumeric: 'tabular-nums' }}
                    placeholder="010-1234-5678"
                    required
                    autoComplete="tel"
                    inputMode="tel"
                    enterKeyHint="send"
                    maxLength={13}
                />
            </KitField>
            <FieldError message={phoneError} />
            <p className="mt-2 text-[12px] text-[#8B8B94]">SMS로 인증번호가 전송됩니다</p>

            <div className="mt-6">
                <KitPrimaryButton type="submit" disabled={!canSend}>
                    {sending ? (
                        <><Spinner dark={false} /><span>전송 중...</span></>
                    ) : (
                        <><MessageSquare className="h-[18px] w-[18px]" /><span>인증번호 전송</span></>
                    )}
                </KitPrimaryButton>
            </div>

            <div className="mt-7 flex items-center justify-center gap-2 border-t border-white/[0.07] pt-5 text-[14px]">
                <span className="text-[#71717A]">{isSignUp ? '이미 계정이 있으신가요?' : '처음 이용하시나요?'}</span>
                <button type="button" onClick={onToggleMode} className="font-semibold text-[#FAFAFA] underline-offset-4 hover:underline">
                    {isSignUp ? '로그인' : '회원가입'}
                </button>
            </div>
        </form>
    );
};

export interface AuthOtpFormProps {
    otp: string;
    onOtpChange: (raw: string) => void;
    onOtpBlur: () => void;
    otpError?: string;
    showOtp: boolean;
    onToggleShowOtp: () => void;
    isExistingUser: boolean;
    onConsentChange: (allAgreed: boolean) => void;
    onOpenTerms: () => void;
    isFormValid: boolean;
    verifyLoading: boolean;
    sending: boolean;
    resendCooldown: number;
    otpSent: boolean;
    onSubmit: (e: React.FormEvent) => void;
    onBack: () => void;
    onResend: () => void;
}

/** 2단계 — 6자리 인증번호(+새 손님은 가입 동의) 넣고 [인증 완료] */
export const AuthOtpForm: React.FC<AuthOtpFormProps> = ({
    otp, onOtpChange, onOtpBlur, otpError, showOtp, onToggleShowOtp, isExistingUser, onConsentChange, onOpenTerms,
    isFormValid, verifyLoading, sending, resendCooldown, otpSent, onSubmit, onBack, onResend,
}) => {
    const reduce = useReducedMotion();
    const canVerify = isFormValid && !verifyLoading;
    const resendLocked = resendCooldown > 0 || sending;
    return (
        <form onSubmit={onSubmit} className="mt-8">
            <KitLabel htmlFor="auth-otp">인증번호</KitLabel>
            <KitField
                className="mt-2"
                icon={<Shield className="h-5 w-5" />}
                state={otpError ? 'error' : 'default'}
                right={
                    <button
                        type="button"
                        onClick={onToggleShowOtp}
                        className="flex h-9 w-9 items-center justify-center rounded-full text-[#71717A] transition-colors hover:text-[#FAFAFA]"
                        aria-label={showOtp ? '인증번호 가리기' : '인증번호 보기'}
                    >
                        {showOtp ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                }
            >
                <input
                    id="auth-otp"
                    type={showOtp ? 'text' : 'password'}
                    value={otp}
                    onChange={(e) => onOtpChange(e.target.value)}
                    onBlur={onOtpBlur}
                    className="w-full bg-transparent text-center font-mono text-[22px] tracking-[0.3em] text-[#FAFAFA] placeholder-[#6B6B74] focus:outline-none"
                    placeholder="••••••"
                    required
                    autoComplete="one-time-code"
                    inputMode="numeric"
                    enterKeyHint="done"
                    maxLength={6}
                />
            </KitField>
            <FieldError message={otpError} />
            <p className="mt-2 text-[12px] text-[#8B8B94]">전송된 6자리 숫자를 입력해주세요</p>

            {/* 스팸함 안내 — 보내자마자 표시(대표 7685·7687) */}
            <div className="mt-4">
                <OtpSpamHint />
            </div>

            {/* 회원가입 동의(이용약관·개인정보 수집·이용·제3자 제공) — 신규 가입자에게만 */}
            {!isExistingUser && (
                <div className="mt-5">
                    <SignupConsentChecklist onChange={onConsentChange} onOpenTerms={onOpenTerms} showWarning={otp.length === 6} />
                </div>
            )}

            <div className="mt-6">
                <KitPrimaryButton type="submit" disabled={!canVerify}>
                    {verifyLoading ? (
                        <><Spinner dark={false} /><span>확인 중...</span></>
                    ) : (
                        <span>인증 완료</span>
                    )}
                </KitPrimaryButton>
            </div>

            <div className="mt-3 grid grid-cols-2 gap-3">
                <KitSecondaryButton onClick={onBack}>뒤로가기</KitSecondaryButton>
                <KitSecondaryButton onClick={onResend} disabled={resendLocked}>
                    <span style={{ fontVariantNumeric: 'tabular-nums' }}>{resendCooldown > 0 ? `재전송 (${resendCooldown}s)` : '재전송'}</span>
                </KitSecondaryButton>
            </div>

            {/* 이메일 가입으로 가던 '인증번호를 받지 못하셨나요?' 연결은 닫음 — 2026-11-03 대표 "다 전화번호로 해야 돼" */}
        </form>
    );
};
